import { AlertCircle, CheckCircle2, Upload, X } from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useSupplierDisplay } from '@/contexts/supplier-display-context';
import { useFileUpload } from '@/hooks/use-file-upload';
import { formatFileSize } from '@/lib/utils';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

interface SupplierFileUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  partyId: string; // Required: ID of the party uploading the file
  actingOnBehalfClientName?: string;
}

export const SupplierFileUploadModal: React.FC<SupplierFileUploadModalProps> = ({
  open,
  onClose,
  onUploadSuccess,
  partyId,
  actingOnBehalfClientName,
}) => {
  const { labels } = useSupplierDisplay();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { upload, isUploading, isProcessing, error, processingError, reset } = useFileUpload();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      reset();
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      reset();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('No file selected', {
        description: 'Please select a file to upload',
      });
      return;
    }

    if (processingError) return;

    const result = await upload({
      file: selectedFile,
      party_id: partyId,
      file_format_id: 1, // TODO: dynamically set
      ...(description ? { description } : {}),
    });

    if (result.status === 'processed' && result.response) {
      const processing = result.response.processing_result;
      toast.success(`${labels.plural} file uploaded successfully`, {
        description: `Processed ${processing?.processed_successfully || 0} ${labels.plural.toLowerCase()} successfully${
          processing?.failed_rows ? ` (${processing.failed_rows} failed)` : ''
        }`,
      });
      setSelectedFile(null);
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploadSuccess?.();
      onClose();
      return;
    }

    if (result.status === 'uploaded' && result.response) {
      toast.success(`${labels.plural} file uploaded successfully`, {
        description: `${selectedFile.name} was stored and was not processed automatically.`,
      });
      setSelectedFile(null);
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploadSuccess?.();
      onClose();
      return;
    }

    if (result.status === 'processing_failed') {
      toast.error('Processing failed', {
        description: 'The file was uploaded, but processing failed.',
      });
      return;
    }

    toast.error('Upload failed', {
      description: result.error?.message ?? 'Upload failed',
    });
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setDescription('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload {labels.plural} File</DialogTitle>
          <DialogDescription>
            Upload a CSV file containing {labels.plural.toLowerCase()} payment details. The file
            will be validated and processed automatically.
          </DialogDescription>
          {actingOnBehalfClientName && (
            <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 w-fit dark:text-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30">
              Acting on behalf of {actingOnBehalfClientName}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Drag-and-drop zone */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: Drop zone for drag-and-drop file upload */}
          <div
            className={`border-2 border-dashed p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary bg-primary/5'
                : error
                  ? 'border-destructive bg-red-50 dark:bg-red-950/30'
                  : 'border-input'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {selectedFile ? selectedFile.name : 'Drag and drop your CSV file here'}
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedFile
                  ? `${formatFileSize(selectedFile.size)} • ${selectedFile.type || 'text/csv'}`
                  : 'or click below to browse'}
              </p>
            </div>
          </div>

          {/* File input */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="shrink-0"
                data-choose-file=""
              >
                Choose File
              </Button>
              <span className="text-sm text-muted-foreground truncate flex-1">
                {selectedFile ? selectedFile.name : 'No file chosen'}
              </span>
              {selectedFile && !isUploading && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    // Move focus before this button unmounts so the dialog never takes it.
                    e.currentTarget.parentElement
                      ?.querySelector<HTMLButtonElement>('[data-choose-file]')
                      ?.focus();
                    setSelectedFile(null);
                    reset();
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="shrink-0"
                  aria-label="Clear selected file"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              accept=".csv,.txt" // TODO: should be dynamic based on supported formats
              disabled={isUploading}
              className="hidden"
            />
            <p className="text-xs text-muted-foreground">
              Supported formats: CSV, TXT (comma or tab-delimited)
            </p>
          </div>

          {/* Description field */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Add notes about this upload..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isUploading}
              rows={3}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Upload failed</p>
                <p className="text-sm text-red-700">{error.message}</p>
              </div>
            </div>
          )}

          {processingError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  Upload succeeded, but processing failed
                </p>
                <p className="text-sm text-red-700">{processingError.message}</p>
              </div>
            </div>
          )}

          {/* Success indicator */}
          {selectedFile && !error && !processingError && !isUploading && !isProcessing && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <p className="text-sm text-green-700">File ready to upload</p>
            </div>
          )}

          {/* Upload progress */}
          {(isUploading || isProcessing) && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-2 w-2 bg-blue-600 rounded-full animate-pulse" />
                <p className="text-sm font-medium text-blue-800">
                  {isUploading ? 'Uploading...' : 'Processing...'}
                </p>
              </div>
              <p className="text-xs text-blue-700">
                {isUploading
                  ? 'Uploading file to server...'
                  : `Processing and validating your ${labels.plural.toLowerCase()} data...`}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading || isProcessing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading || isProcessing || processingError !== null}
          >
            {isUploading ? 'Uploading...' : isProcessing ? 'Processing...' : 'Upload & Process'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
